import {
  AcceptBookingResponseSchema,
  ApiErrorSchema,
  ConnectStatusResponseSchema,
  OnboardingLinkResponseSchema,
  PayoutListResponseSchema,
  RejectBookingResponseSchema,
  type AcceptBookingResponse,
  type ConnectStatusResponse,
  type OnboardingLinkResponse,
  type PayoutListResponse,
  type RejectBookingResponse,
} from '@ah/contracts';

/** Thin fetch wrapper for the payments endpoints — same shape as the other apps' *.api.ts files. */
export class PaymentsApi {
  constructor(private readonly baseUrl: string) {}

  async getConnectStatus(accessToken: string): Promise<ConnectStatusResponse> {
    const res = await fetch(`${this.baseUrl}/api/v1/payments/connect/status`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) throw await this.toError(res);
    return ConnectStatusResponseSchema.parse(await res.json());
  }

  async createOnboardingLink(accessToken: string): Promise<OnboardingLinkResponse> {
    const res = await fetch(`${this.baseUrl}/api/v1/payments/connect/onboarding-link`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) throw await this.toError(res);
    return OnboardingLinkResponseSchema.parse(await res.json());
  }

  /** Captures the client's held payment authorization — ADR-010's manual-capture flow. */
  async acceptBooking(accessToken: string, bookingId: string): Promise<AcceptBookingResponse> {
    const res = await fetch(`${this.baseUrl}/api/v1/payments/bookings/${bookingId}/accept`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) throw await this.toError(res);
    return AcceptBookingResponseSchema.parse(await res.json());
  }

  /** Releases the client's held payment authorization — zero cost. */
  async rejectBooking(accessToken: string, bookingId: string, reason?: string): Promise<RejectBookingResponse> {
    const res = await fetch(`${this.baseUrl}/api/v1/payments/bookings/${bookingId}/reject`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ reason }),
    });
    if (!res.ok) throw await this.toError(res);
    return RejectBookingResponseSchema.parse(await res.json());
  }

  async listMyPayouts(accessToken: string): Promise<PayoutListResponse> {
    const res = await fetch(`${this.baseUrl}/api/v1/payments/payouts/my`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) throw await this.toError(res);
    return PayoutListResponseSchema.parse(await res.json());
  }

  private async toError(res: Response): Promise<Error> {
    try {
      const payload = ApiErrorSchema.parse(await res.json());
      return new PaymentsApiError(payload.error.code, payload.error.message, res.status);
    } catch {
      return new PaymentsApiError('INTERNAL_ERROR', res.statusText, res.status);
    }
  }
}

export class PaymentsApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'PaymentsApiError';
  }
}
